import type { CloudAPI } from '@yandex-360/telemost-sdk/cloud-api';
import { call } from 'typed-redux-saga';

import { GRID_VIEW } from '../../../pages/call/constants';
import { BroadcastStatus } from '../../../types';
import { cloudApi } from '../../helpers/transport';
import { ConferenceConfigOptions } from './types';

interface CreatedBroadcastData extends CloudAPI.CreateBroadcast.Response {
    status: BroadcastStatus;
}

interface BroadcastManagerConfig {
    conferenceConfigOptions: ConferenceConfigOptions;
    uri: string;
    broadcastUri?: string;
}

export class BroadcastManager {
    private conferenceConfigOptions: Maybe<ConferenceConfigOptions>;
    private uri: Maybe<string>;
    private broadcastUri: Maybe<string>;

    public init(config: BroadcastManagerConfig) {
        this.uri = config.uri;
        this.setBroadcastUri(config.broadcastUri);
        this.conferenceConfigOptions = config.conferenceConfigOptions;
    }

    public get isReady(): boolean {
        return Boolean(this.uri);
    }

    public setBroadcastUri(broadcastUri?: string) {
        this.broadcastUri = broadcastUri;
    }

    public *createBroadcast(broadcastData: CloudAPI.CreateBroadcast.Request) {
        if (!this.uri) {
            return;
        }

        const result = yield* call([cloudApi, cloudApi.createBroadcast], this.uri, broadcastData);

        const resultBroadcastData: CreatedBroadcastData = {
            status: BroadcastStatus.CREATED,
            ...result,
        };

        this.setBroadcastUri(result.broadcast_uri);

        return resultBroadcastData;
    }

    public *startBroadcast(view: GRID_VIEW) {
        if (!this.uri) {
            return;
        }

        if (!this.broadcastUri) {
            throw new Error('No broadcast_uri');
        }

        const response = yield* call([cloudApi, cloudApi.startBroadcast], this.uri, this.broadcastUri, view);

        return response;
    }

    public *stopBroadcast() {
        if (!this.uri) {
            return;
        }

        if (!this.broadcastUri) {
            throw new Error('No broadcast_uri');
        }

        return yield* call([cloudApi, cloudApi.stopBroadcast], this.uri, this.broadcastUri);
    }

    public *endBroadcast() {
        if (!this.uri) {
            return;
        }

        if (!this.broadcastUri) {
            throw new Error('No broadcast_uri');
        }

        return yield* call([cloudApi, cloudApi.endBroadcast], this.uri, this.broadcastUri);
    }

    public *setViewInBroadcast(view: GRID_VIEW) {
        if (!this.uri) {
            return;
        }

        if (!this.broadcastUri) {
            throw new Error('No broadcast_uri');
        }

        yield* call([cloudApi, cloudApi.setViewInBroadcast], this.uri, this.broadcastUri, view);
    }

    public reinit(config: BroadcastManagerConfig) {
        this.destroy();
        this.init(config);
    }

    public destroy() {
        this.conferenceConfigOptions = undefined;
        this.uri = undefined;
        this.setBroadcastUri(undefined);
    }
}
